// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/client';
import { FormRow, SaveBar, Section, SectionRail } from './primitives';
import { emailFormFromSettings, changedEmailFields } from './emailSettingsForm';

/* Communications — sending identity → phone numbers → templates → status.
   Template editing is a ROUTE in the same column
   (/settings/communications/templates/:id) — same shell, same save bar,
   back link to the list; the list stays mounted underneath. */

const SECTIONS = [
  { id: 'identity', label: 'Sending identity' },
  { id: 'numbers', label: 'Phone numbers' },
  { id: 'automated', label: 'Automated messaging' },
  { id: 'templates', label: 'Templates' },
  { id: 'status', label: 'Sending status' },
];

const MERGE_FIELDS = ['{{first_name}}', '{{name}}'];
const SAMPLE = { first_name: 'Jane', name: 'Jane Doe' };
const renderSample = (text) =>
  String(text || '').replace(/\{+(\w+)\}+/g, (m, key) => SAMPLE[key] ?? `{{${key}}}`);
const templateSubject = (t) => t?.subject || t?.metadata?.subject || '';

function TemplateEditor({ id, templates, onDone, reload }) {
  const isNew = id === 'new';
  const source = useMemo(() => {
    if (isNew) return { id: null, name: '', subject: '', body: 'Hi {{first_name}},\n\n' };
    const t = templates.find((row) => String(row.id) === String(id));
    return t
      ? { id: t.id, name: t.name, subject: templateSubject(t), body: t.body }
      : null;
  }, [id, isNew, templates]);

  const [form, setForm] = useState(source);
  const [baseline, setBaseline] = useState(source);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(source); setBaseline(source); }, [source]);

  const dirty = useMemo(
    () => form && JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline]
  );

  if (!form) {
    return (
      <div className="sv2-col">
        <button className="sv2-back" onClick={onDone}>← All templates</button>
        <p className="sv2-section-sub">That template isn't here anymore.</p>
      </div>
    );
  }

  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) {
      toast.error('Name and message are required');
      return;
    }
    setSaving(true);
    const payload = {
      name: form.name.trim(), subject: form.subject, body: form.body,
      channel: 'email', metadata: { subject: form.subject },
    };
    try {
      if (isNew) await api.post('/templates/templates/', payload);
      else await api.patch(`/templates/templates/${form.id}/`, payload);
      toast.success(isNew ? 'Template created' : 'Template saved');
      reload();
      onDone();
    } catch (e) {
      const detail = e?.response?.data;
      toast.error(detail?.name?.[0] || detail?.detail || 'Could not save — is the name unique?');
    } finally {
      setSaving(false);
    }
  };

  const destroy = async () => {
    if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/templates/templates/${form.id}/`);
      toast.success('Deleted');
      reload();
      onDone();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="sv2-col">
      <button className="sv2-back" onClick={onDone}>← All templates</button>
      <Section id="template" title={isNew ? 'New template' : form.name}
        sub="Merge fields fill in for each customer automatically.">
        <FormRow label="Name" help="How your team finds it in the list.">
          <input type="text" value={form.name} placeholder="e.g. Quick check-in"
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
        </FormRow>
        <FormRow label="Subject" help="The subject line customers see.">
          <input type="text" value={form.subject}
            onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))} />
        </FormRow>
        <FormRow label="Message" help="Use the buttons to drop in a customer's name.">
          <div style={{ display: 'grid', gap: 6 }}>
            <div className="sv2-actions">
              {MERGE_FIELDS.map((f) => (
                <button key={f} className="sv2-btn sm"
                  onClick={() => setForm((p) => ({ ...p, body: p.body + f }))}>{f}</button>
              ))}
            </div>
            <textarea rows={8} value={form.body}
              style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg-2)', border: '1px solid var(--border-1)', borderRadius: 8, color: 'var(--text-1)', fontSize: 13, padding: '8px 10px', fontFamily: 'inherit', resize: 'vertical' }}
              onChange={(e) => setForm((p) => ({ ...p, body: e.target.value }))} />
          </div>
        </FormRow>
        <div className="sv2-preview">
          <div className="sv2-help" style={{ marginBottom: 6 }}>Preview with a sample customer:</div>
          <div className="sv2-preview-subject">{renderSample(form.subject) || '(no subject)'}</div>
          <div className="sv2-preview-body">{renderSample(form.body)}</div>
        </div>
        {!isNew && (
          <div className="sv2-actions">
            <button className="sv2-btn danger" onClick={destroy}>Delete this template</button>
          </div>
        )}
      </Section>
      <SaveBar dirty={!!dirty} saving={saving} onSave={save}
        onDiscard={() => (isNew ? onDone() : setForm(baseline))} />
    </div>
  );
}

export default function CommunicationsTab({ sub, id }) {
  const navigate = useNavigate();
  const [identity, setIdentity] = useState(null);
  const [emailCfg, setEmailCfg] = useState(null);
  const [twilio, setTwilio] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({ from_local_part: '', reply_to: '' });
  const [baseline, setBaseline] = useState({ from_local_part: '', reply_to: '' });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [senderName, setSenderName] = useState('');
  const [senderBaseline, setSenderBaseline] = useState('');
  const [savingSender, setSavingSender] = useState(false);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(baseline), [form, baseline]);
  const editing = sub === 'templates' && id;

  const loadTemplates = useCallback(() => {
    api.get('/templates/templates/').then((r) => {
      const rows = r.data?.results || (Array.isArray(r.data) ? r.data : []);
      setTemplates(rows.filter((t) => t.channel === 'email'));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/comms/sending-identity/').then((r) => setIdentity(r.data)).catch(() => {});
    api.get('/accounts/email-settings/').then((r) => {
      setEmailCfg(r.data);
      const seed = emailFormFromSettings(r.data);
      setForm(seed); setBaseline(seed);
    }).catch(() => {});
    api.get('/accounts/twilio-settings/').then((r) => setTwilio(r.data)).catch(() => {});
    api.get('/accounts/preferences/').then((r) => {
      const p = r.data?.preferences || {};
      setPrefs(p);
      const s = String(p?.identity?.sender_name || '');
      setSenderName(s); setSenderBaseline(s);
    }).catch(() => {});
    loadTemplates();
  }, [loadTemplates]);

  const saveSenderName = async () => {
    const next = senderName.trim();
    if (next === senderBaseline) return;
    setSavingSender(true);
    const nextIdentity = { ...(prefs?.identity || {}), sender_name: next };
    try {
      await api.patch('/accounts/preferences/', { preferences: { ...(prefs || {}), identity: nextIdentity } });
      setPrefs((p) => ({ ...(p || {}), identity: nextIdentity }));
      setSenderBaseline(next);
      toast.success('Sender name saved');
    } catch {
      toast.error('Could not save sender name');
    } finally {
      setSavingSender(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/accounts/email-settings/', changedEmailFields(form, baseline));
      setEmailCfg(data);
      const next = emailFormFromSettings(data);
      setForm(next); setBaseline(next);
      api.get('/comms/sending-identity/').then((r) => setIdentity(r.data)).catch(() => {});
      toast.success('Saved');
    } catch {
      toast.error('Could not save — check the fields');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post('/accounts/email-settings/test/');
      toast.success(data?.detail || 'Test email sent — check the inbox');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setTesting(false);
    }
  };

  const duplicateTemplate = async (t) => {
    try {
      await api.post('/templates/templates/', {
        name: `${t.name} (copy)`, subject: templateSubject(t), body: t.body,
        channel: 'email', metadata: { subject: templateSubject(t) },
      });
      toast.success('Duplicated');
      loadTemplates();
    } catch {
      toast.error('Could not duplicate — a copy may already exist');
    }
  };

  /* Editor route: same shell, same column, back to the list. The list and
     its data stay mounted in this component — nothing resets on back. */
  const ft = prefs?.first_touch || {};
  const [ftTone, setFtTone] = useState(String(ft.tone || ''));
  const [ftMin, setFtMin] = useState(String(Math.round((ft.delay_min_seconds || 180) / 60)));
  const [ftMax, setFtMax] = useState(String(Math.round((ft.delay_max_seconds || 480) / 60)));
  useEffect(() => {
    const f = prefs?.first_touch || {};
    setFtTone(String(f.tone || ''));
    setFtMin(String(Math.round((f.delay_min_seconds || 180) / 60)));
    setFtMax(String(Math.round((f.delay_max_seconds || 480) / 60)));
  }, [prefs?.first_touch]);
  const saveFirstTouch = async (patch) => {
    const next = { ...(prefs?.first_touch || {}), ...patch };
    try {
      await api.patch('/accounts/preferences/', { preferences: { ...(prefs || {}), first_touch: next } });
      setPrefs((p) => ({ ...(p || {}), first_touch: next }));
      toast.success('Saved');
    } catch { toast.error('Could not save'); }
  };

  if (editing) {
    return (
      <div className="sv2-body">
        <TemplateEditor id={id} templates={templates} reload={loadTemplates}
          onDone={() => navigate('/settings/communications')} />
      </div>
    );
  }

  // Automated-messaging toggles — each is its OWN per-tenant switch, saved
  // immediately (independent of the email SaveBar). Writes to preferences.comms;
  // the backend syncs the flag onto TenantCommsSettings (the declared consumer
  // the first-touch signal and the auto-responder read).
  const firstTouchOn = !!prefs?.comms?.auto_first_touch_enabled;
  const autoResponderOn = !!prefs?.comms?.auto_responder_enabled;
  const toggleComms = async (key, next) => {
    const nextComms = { ...(prefs?.comms || {}), [key]: next };
    setPrefs((p) => ({ ...(p || {}), comms: nextComms })); // optimistic
    try {
      await api.patch('/accounts/preferences/', { preferences: { ...(prefs || {}), comms: nextComms } });
      toast.success('Saved');
    } catch {
      setPrefs((p) => ({ ...(p || {}), comms: { ...(p?.comms || {}), [key]: !next } })); // revert
      toast.error('Could not save');
    }
  };

  const domainVerified = emailCfg?.domain_status === 'verified';
  const a2pApproved = !!prefs?.sms?.a2p_approved;
  const caps = twilio?.capabilities || {};
  const capsOn = Object.keys(caps).filter((k) => caps[k]);
  const smsAutomation = capsOn.length > 0;
  const outreachOn = !!prefs?.outreach?.enabled;
  const sweepOn = !!prefs?.billing?.sweep_enabled;
  const numbers = twilio?.numbers || [];

  return (
    <div className="sv2-body">
      <SectionRail sections={SECTIONS} />
      <div className="sv2-col">
        <Section id="identity" title="Sending identity"
          sub="Who your emails come from — exactly what customers see.">
          <FormRow label="Sender name"
            help="The person your outreach signs as — e.g. 'Dan'. AI drafts and follow-ups say 'it's {name} at {business}'. Required: without it, outreach won't generate (it will never borrow another business's name).">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="text" value={senderName} placeholder="e.g. Dan"
                onChange={(e) => setSenderName(e.target.value)}
                onBlur={saveSenderName} />
              {savingSender && <span className="sv2-help">saving…</span>}
            </div>
          </FormRow>
          <FormRow label="From" value={identity?.from_address} mono
            help="Every email your team and automations send." />
          <FormRow label="Replies go to" value={identity?.reply_to} mono
            help="Replies land in your CRM inbox thread." />
          <FormRow label="Domain"
            help="Your sending domain, verified for deliverability.">
            <div className="sv2-value mono" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              {emailCfg?.mailgun_domain || emailCfg?.platform_domain || '—'}
              <span className={`sv2-pill ${domainVerified ? 'ok' : 'warn'}`}>
                {domainVerified ? 'Verified' : 'Pending'}
              </span>
            </div>
          </FormRow>
          <FormRow label="From address"
            help={`The part before @${emailCfg?.root_domain || 'your domain'} — e.g. hello, office, quotes.`}>
            <input type="text" value={form.from_local_part}
              onChange={(e) => setForm((p) => ({ ...p, from_local_part: e.target.value }))} />
          </FormRow>
          <FormRow label="Reply-to email"
            help="A monitored inbox. Leave blank to keep replies in the CRM.">
            <input type="email" value={form.reply_to} placeholder="office@yourdomain.com"
              onChange={(e) => setForm((p) => ({ ...p, reply_to: e.target.value }))} />
          </FormRow>
          <div className="sv2-actions">
            <button className="sv2-btn" onClick={sendTest} disabled={testing}>
              {testing ? 'Sending…' : 'Send me a test email'}
            </button>
          </div>
        </Section>

        <Section id="numbers" title="Phone numbers"
          sub="Your business lines for calls and texting.">
          <div className="sv2-obj-list">
            {numbers.length === 0 && (
              <p className="sv2-section-sub">No numbers yet — add one to start calling and texting from Abon.</p>
            )}
            {numbers.map((n) => (
              <div className="sv2-obj" key={n.id || n.phone_number}>
                <div className="sv2-obj-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="sv2-obj-title">{n.phone_number}</div>
                    <div className="sv2-obj-sub">
                      {n.label || n.region || 'Business line'}{n.is_primary ? ' · primary' : ''}
                    </div>
                  </div>
                  <div className="sv2-actions">
                    <span className="sv2-pill info">{(n.kind || 'sms').replace('both', 'calls + texts')}</span>
                    <span className={`sv2-pill ${a2pApproved ? 'ok' : 'warn'}`}>
                      {a2pApproved ? 'Texting approved' : 'Texting approval in review'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <FormRow label="Customer sign-in codes"
            help="How portal sign-in codes reach your customers."
            value={a2pApproved && numbers.length
              ? 'Text message from your number'
              : 'Email — switches to text once texting approval completes'} />
          <div className="sv2-actions">
            <button className="sv2-btn"
              onClick={() => toast.info('Number setup is moving here — for now your team can add numbers from the phone settings page.')}>
              + Add a number
            </button>
          </div>
        </Section>

        <Section id="templates" title="Templates"
          sub="Saved messages your team and the outreach composer reuse.">
          <div className="sv2-obj-list">
            {templates.length === 0 && (
              <p className="sv2-section-sub">No templates yet — create one, or save a message from the composer.</p>
            )}
            {templates.map((t) => (
              <div className="sv2-obj" key={t.id}>
                <div className="sv2-obj-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="sv2-obj-title">{t.name}</div>
                    <div className="sv2-obj-sub">{templateSubject(t) || t.body}</div>
                  </div>
                  <div className="sv2-actions">
                    <button className="sv2-btn sm"
                      onClick={() => navigate(`/settings/communications/templates/${t.id}`)}>
                      Edit
                    </button>
                    <button className="sv2-btn sm" onClick={() => duplicateTemplate(t)}>Duplicate</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="sv2-actions">
            <button className="sv2-btn primary"
              onClick={() => navigate('/settings/communications/templates/new')}>
              + New template
            </button>
          </div>
        </Section>

        <Section id="automated" title="Automated messaging"
          sub="Per-tenant switches for automatic outreach. Each is independent and off by default. Customer-facing sends still respect quiet hours, do-not-contact, and texting approval.">
          <div>
            <div className="sv2-status-row">
              <span className="sv2-label">First-touch SMS</span>
              <button type="button"
                className={`sv2-pill ${firstTouchOn ? 'ok' : 'off'}`}
                onClick={() => toggleComms('auto_first_touch_enabled', !firstTouchOn)}
                aria-pressed={firstTouchOn}>
                {firstTouchOn ? 'On' : 'Off'}
              </button>
              <span className="sv2-status-copy">A composed opener to a new WEB-FORM lead — written from what they asked for, sent a few minutes later (after hours: a short acknowledgement now, the message when you open), signed as your sender name, with your booking link or a promise to call. Manual, door-knock, and agent-created leads never get it.</span>
            </div>
            {firstTouchOn ? (
              <div className="sv2-substack">
                <FormRow label="Voice note" help="Optional. How you'd say it — e.g. 'casual, first names, no exclamation marks'. Every opener is composed within it.">
                  <textarea rows={2} value={ftTone} onChange={(e) => setFtTone(e.target.value)} onBlur={() => saveFirstTouch({ tone: ftTone })}
                    placeholder="e.g. Friendly and short. Say we're local to Cape Coral." />
                </FormRow>
                <FormRow label="Send after" help="Minutes after the form comes in, inside business hours. Defaults to 3–8.">
                  <span className="sv2-inline">
                    <input type="number" min={1} max={60} value={ftMin} onChange={(e) => setFtMin(e.target.value)} onBlur={() => saveFirstTouch({ delay_min_seconds: Number(ftMin) * 60 })} /> to
                    <input type="number" min={1} max={120} value={ftMax} onChange={(e) => setFtMax(e.target.value)} onBlur={() => saveFirstTouch({ delay_max_seconds: Number(ftMax) * 60 })} /> min
                  </span>
                </FormRow>
                {!prefs?.identity?.sender_name ? <p className="sv2-warn">Set a sender name above — without it the opener is never sent and the lead goes to your office queue instead.</p> : null}
              </div>
            ) : null}
            <div className="sv2-status-row">
              <span className="sv2-label">Auto-reply</span>
              <button type="button"
                className={`sv2-pill ${autoResponderOn ? 'ok' : 'off'}`}
                onClick={() => toggleComms('auto_responder_enabled', !autoResponderOn)}
                aria-pressed={autoResponderOn}>
                {autoResponderOn ? 'On' : 'Off'}
              </button>
              <span className="sv2-status-copy">AI-drafted replies to inbound messages. Separate from first-touch — a reply to them, not an outbound on creation.</span>
            </div>
          </div>
        </Section>

        <Section id="status" title="Sending status"
          sub="Where sending stands today. These switch on deliberately with your go-ahead — not from this page.">
          <div>
            <div className="sv2-status-row">
              <span className="sv2-label">Bulk outreach</span>
              <span className={`sv2-pill ${outreachOn ? 'ok' : 'off'}`}>{outreachOn ? 'On' : 'Off'}</span>
              <span className="sv2-status-copy">Segment email sends from the pipeline composer.</span>
            </div>
            <div className="sv2-status-row">
              <span className="sv2-label">Automated messaging</span>
              <span className={`sv2-pill ${smsAutomation ? 'ok' : 'off'}`}>{smsAutomation ? 'On' : 'Off'}</span>
              <span className="sv2-status-copy">{smsAutomation ? `Enabled: ${capsOn.map((k) => k.replace(/_/g, ' ')).join(', ')}.` : 'Every automated send is off for this account. Messages you send yourself always go.'}</span>
            </div>
            <div className="sv2-status-row">
              <span className="sv2-label">Billing reminders</span>
              <span className={`sv2-pill ${sweepOn ? 'warn' : 'off'}`}>{sweepOn ? 'Pending final check' : 'Off'}</span>
              <span className="sv2-status-copy">Automatic invoice reminders; enabled together with your accountant review.</span>
            </div>
          </div>
        </Section>
      </div>

      <SaveBar dirty={dirty} saving={saving} onSave={save}
        onDiscard={() => setForm(baseline)} />
    </div>
  );
}
